/*
 * Copyright (c) 2026 CalAmp Corp.  All Rights Reserved
 */
import React, {memo} from 'react';
import {makeStyles} from '@material-ui/core/styles';
import {useIsPhone} from '../../../hooks/useMedia';
import {dlsColor} from '../../../theme/theme';
import {AssetSummaryCardIdentity} from '../../assetcard/current/AssetSummaryCardIdentity';
import {
  AssetSummaryCardStatus,
  AssetSummaryCardStatusProps
} from '../../assetcard/current/AssetSummaryCardStatus';
import {ASSET_DETAILS_CARD_WIDTH} from './assetDetailsAccordionStyles';

interface AssetNameStatusHeaderProps {
  assetType: string;
  assetName: string;
  alias?: string;
  status: AssetSummaryCardStatusProps;
}

const useStyles = makeStyles(() => ({
  card : {
    boxSizing       : 'border-box',
    display         : 'flex',
    width           : ASSET_DETAILS_CARD_WIDTH,
    height          : '112px',
    flexDirection   : 'column',
    alignItems      : 'flex-start',
    backgroundColor : dlsColor('white'),
    padding         : '24px 16px 0px',
    borderBottom    : `2px solid ${dlsColor('pastelTeal')}`,
    // the details below are placed at a fixed offset - at some browser zoom
    // levels they land a fraction of a pixel over this card, and painted on
    // top of its bottom border, hid it. Stacking the card above keeps the
    // border in view ...
    position        : 'relative',
    zIndex          : 1
  },
  header : {
    display    : 'flex',
    width      : '100%',
    minWidth   : 0,
    minHeight  : '44px',
    alignItems : 'flex-start',
    gap        : '16px',
    flexGrow   : 1
  }
}));

/**
 * AssetNameStatusHeader renders the ION-7461 Asset Details Drawer's "Asset
 * Name & Status" card - built from the Asset Summary Card header
 * (ION-7392/7393/7395) and following the same dynamic sizing and
 * text-wrapping rules, at this drawer's fixed 456px card width.
 *
 * Unlike the Asset List Card, the Asset Name here is no longer a Link - all
 * previous link states (hover/visited/focus) are removed, per this ticket's
 * Figma spec - achieved by passing `isLink={false}` to the shared
 * `AssetSummaryCardIdentity`. The Status side (state tag, date/time, speed)
 * is reused directly from `AssetSummaryCardStatus`, with the State Tag label
 * shown as translated (not uppercased) in this drawer.
 *
 * ```typescript
 *
 *   import {AssetNameStatusHeader} from
 *     '@ion/dls/src/components/assetdetails/current/AssetNameStatusHeader';
 *   ...
 *
 *   <AssetNameStatusHeader assetType={AssetType.Vehicle}
 *     assetName='Vehicle 13 Vision'
 *     alias='004013982 - HUMAN SERVICES - 21-0763'
 *     status={{
 *       stateLabel: 'Moving (no Ignition)',
 *       stateTextColor: dlsTextColor('orange'),
 *       stateBackgroundColor: alpha(dlsColor('orange'), 0.1),
 *       dateTime: '12/16/2025 - 11:08 AM CST',
 *       speed: '11 MPH'
 *     }} />
 *
 * ```
 *
 */
export const AssetNameStatusHeader = memo(({
  assetType,
  assetName,
  alias,
  status
}: AssetNameStatusHeaderProps) => {
  const classes = useStyles();
  const isPhone = useIsPhone();

  return (
    <div className={classes.card}>
      <div className={classes.header}>
        {/* one line each, with the full name on hover once it is cut off ... */}
        <AssetSummaryCardIdentity assetType={assetType}
          assetName={assetName}
          alias={alias}
          isLink={false}
          lines={1} />
        {/* the tag tops level with the name ... */}
        <AssetSummaryCardStatus {...status}
          keepLabelCase
          topAligned
          hugWidth={isPhone} />
      </div>
    </div>
  );
});

export default AssetNameStatusHeader;
